import { PlusOutlined } from '@ant-design/icons';

import { Button, Collapse, Layout, Row } from "antd";
import Sider from "antd/es/layout/Sider";
import { Content, Header } from "antd/es/layout/layout";
import { useState } from 'react';
import { DataViewProps } from './DataView';
import { EditableProps } from "./Editable";
import { ToggleableProps } from "./Toggleable";

export type ListPropsParams<T> = DataViewProps<T[]> & {
    getItemKey?: (Item: T) => string,
};

export type ListProps<T> = ListPropsParams<T> & {
    setItem: (item: T) => void,
    deleteItem: (item: T) => void,
    setItemOrDeleteOld: (item: T | undefined, old: T) => void,
    getItemKey: (Item: T) => string,
    onAddItem?: () => void,
    onEditItem?: (item: T) => void,
    onSelectedItem?: (Item: T) => void
};

export function useList<T>(props:
    ListPropsParams<T>
): ListProps<T> {

    const _props = props as ListProps<T>;

    const findIndex = (item: T) => {
        if (!props.data) return undefined;
        let key = _props.getItemKey(item);
        return props.data.findIndex((i: T) => _props.getItemKey(i) === key);
    }
    
    _props.getItemKey = _props.getItemKey || ((item: T) => {
        const i = item as any;
        if (!('id' in i)) {
            throw new Error('You must define a getItemKey when item does not have an id property');
        }
        if (!i.id) throw new Error('item.id is undefined');
        if (typeof i.id === 'string') return i.id;
        if (typeof i.id === 'number') return i.id.toString();
        return JSON.stringify(i.id);
    });
    
    _props.setItem = (item: T) => {
        let data = (props.data && [...props.data]) || [];
        let idx = findIndex(item);
        if (idx === undefined || idx < 0) {
            data.push({ ...item }); // item created
        } else {
            data[idx] = { ...item }; // item updated"
        }
        props.setData(data);
    }

    _props.deleteItem = (item: T) => {
        if (!props.data) return;
        let idx = findIndex(item);
        if (idx !== undefined) {
            props.data.splice(idx, 1);
            props.setData([...props.data]);
        }
    }

    _props.setItemOrDeleteOld = (item: T | undefined, old: T) => {
        if (item) _props.setItem(item);
        else _props.deleteItem(old);
    }

    return _props;

}

export function useStatefulList<T>(
    data?: T[],
    getItemKey?: (Item: T) => string,
): ListProps<T> {
    const state = useState<T[] | undefined>(data);
    return useList<T>({ data: state[0], setData: state[1], getItemKey });
}

export function useListWithEditable<T>(props: {
    listProps: ListProps<T>,
    editableProps: EditableProps<T>,
    toggle?: ToggleableProps,
    defaultAddItemData?: T
}) {

    const { listProps, editableProps, toggle } = props;

    const hide = () => { toggle && toggle.hide(); }
    const show = () => { toggle && toggle.show(); }

    listProps.onAddItem = () => {
        let d = props.defaultAddItemData;
        editableProps.edit(d? {...d} : {} as T); show(); // Pass a new instance to force change detection
    };

    listProps.onEditItem = (item: T) => {
        editableProps.edit(item); show();
    };

    editableProps.onSaved = (item: T, hideAfterSaving: boolean) => {
        listProps.setItem(item); 
        if (hideAfterSaving) hide();
    };

    editableProps.onDeleted = (item: T) => {
        listProps.deleteItem(item); hide();
    };

    return props;
}

export function ListWithCreateButtonLayout(props: {
    children: any,
    onCreateClick?: () => void
}) {

    return (
        <Layout className="h-100">
            {props.onCreateClick && (
                <Header hidden={!props.onCreateClick} style={{ paddingBottom: "1em" }}>
                    <Button type="dashed" icon={<PlusOutlined />} onClick={props.onCreateClick} block>Create</Button>
                </Header>
            )}
            <Content>
                <Row gutter={16}>
                    {props.children}
                </Row>
            </Content>
        </Layout>
    );

}

export function SectionListLayout(props: {
    children: any,
    onCreateClick?: () => void,
    title?: string,
    addButtonState?: 'visible' | 'hidden' | 'disabled'
}) {

    const addButtonProps = {} as any;
    switch (props.addButtonState) {
        case 'hidden':
            addButtonProps['style'] = { display: 'none' };
            break;
        case 'disabled':
            addButtonProps['disabled'] = true;
            break;
    }

    return (
        <Collapse ghost className='mig-collapse'
            defaultActiveKey={['1']}
            expandIconPosition={"start"}
            items={[
                {
                    key: '1',
                    label: (
                        <>
                            <span>{props.title || "Sin titulo"}</span><span  > </span>
                            { props.onCreateClick && (
                            <Button {...addButtonProps} type='text' shape="circle" icon={<PlusOutlined />} size="small" onClick={(event) => {
                                props.onCreateClick && props.onCreateClick();
                                event.stopPropagation();
                            }} />
                            )}
                        </>
                    ),
                    children: <Layout >
                        <Content style={{overflowX: 'hidden'}}>
                            <Row gutter={16}>
                                {props.children}
                            </Row>    
                        </Content>
                    </Layout>
                }]}
        />
    );

}

export const ListWithSiderDetailLayout = function (props: {
    list: any,
    detail: any,
    toggle?: ToggleableProps
}) {

    const { toggle } = props;
    return (
        <Layout style={{ height: "100%" }} >
            <Layout style={{ height: "100%" }} >
                <Content style={{overflowY: "auto" }}>
                    {props.list}
                </Content>
            </Layout>
            <Sider width="100%" collapsedWidth={"0"} collapsed={!(toggle && toggle.visible)}>
                {props.detail}
            </Sider>
        </Layout>
    );

}

// TODO Quitar?
export const ListLayout = function (props: {
    list: any,
}) {

    return (
        <Layout style={{ height: "100%" }} >
            <Layout style={{ height: "100%" }} >
                <Content style={{overflowY: "auto" }}>
                    {props.list}
                </Content>
            </Layout>
        </Layout>
    );

}
